import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuthStore } from '../store/authStore';
import { queryCache, CACHE_TTL } from '../lib/queryCache';
import {
  DashboardData,
  ActiveCourseSummary,
  DailyTask,
  ActivityLogItem,
  HeatmapDay,
  ActivityType,
} from '../types/dashboard.types';
import { Course } from '../types/courses.types';

const GRADIENTS: ActiveCourseSummary['gradient'][] = ['gradient-1', 'gradient-2', 'gradient-3'];
const COURSE_ICONS: Record<string, string> = {
  python: '🐍',
  web: '🌐',
  scratch: '🎮',
  ai: '🧠',
  yapay_zeka: '🤖',
  game: '🎮',
  matematik: '📐',
  fen: '🔬',
  ingilizce: '🇬🇧',
  turkce: '📚',
  veri_analizi: '📊',
};

const STATIC_DAILY_TASKS: Omit<DailyTask, 'isCompleted'>[] = [
  { id: 'complete-lesson', title: '1 Ders Tamamla', description: 'Herhangi bir derste ilerleme kaydet', xpReward: 50 },
  { id: 'solve-quiz', title: '3 Quiz Çöz', description: 'Herhangi bir konuda quiz çöz', xpReward: 30 },
  { id: 'ai-chat', title: 'AI Mentor ile Sohbet Et', description: 'En az 5 mesajlık bir konuşma yap', xpReward: 20 },
  { id: 'write-code', title: 'Kod Yaz ve Çalıştır', description: 'Kod editöründe bir program yaz', xpReward: 40 },
];

/** Yaşa göre uygun difficulty seviyelerini döndürür */
function getDifficultyForAge(age: number | null): string[] {
  if (!age) return ['beginner', 'intermediate'];
  if (age <= 9) return ['beginner'];
  if (age <= 12) return ['beginner', 'intermediate'];
  return ['intermediate', 'advanced'];
}

async function buildRealHeatmap(studentId: string, weeks = 20): Promise<HeatmapDay[]> {
  // Isı haritası verisini önbelleğe al (geçmiş veri — oturum içinde nadiren değişir)
  return queryCache.dedupe(`heatmap:${studentId}`, async () => {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - (weeks * 7 - 1));
    const startDateStr = startDate.toISOString().slice(0, 10);

    // Bugünü veritabanında giriş olarak kaydet (arka planda, yüklemeyi bloklamaz)
    const todayStr = new Date().toISOString().slice(0, 10);
    (async () => {
      try {
        const { error } = await supabase.rpc('record_daily_activity', { p_student_id: studentId, p_xp_earned: 0 });
        if (error) {
          // RPC başarısız olduysa fallback olarak doğrudan upsert yap
          await supabase.from('student_daily_activity').upsert(
            { student_id: studentId, activity_date: todayStr, login_count: 1 },
            { onConflict: 'student_id,activity_date' }
          );
        }
      } catch (err) {
        console.error('Günlük aktivite kaydı sırasında hata:', err);
      }
    })();

  const [dailyActsRes, completionsRes, quizAttemptsRes] = await Promise.all([
    supabase
      .from('student_daily_activity')
      .select('activity_date, xp_earned, login_count, lessons_completed, quizzes_completed')
      .eq('student_id', studentId)
      .gte('activity_date', startDateStr),
    supabase
      .from('student_lesson_completions')
      .select('completed_at')
      .eq('student_id', studentId)
      .gte('completed_at', startDateStr),
    supabase
      .from('quiz_attempts')
      .select('completed_at')
      .eq('student_id', studentId)
      .gte('completed_at', startDateStr),
  ]);

  const activityByDate: Record<string, { xp: number; logins: number; lessons: number; quizzes: number }> = {};

  (dailyActsRes.data ?? []).forEach((row: any) => {
    const d = row.activity_date;
    if (!activityByDate[d]) activityByDate[d] = { xp: 0, logins: 0, lessons: 0, quizzes: 0 };
    activityByDate[d].xp += row.xp_earned ?? 0;
    activityByDate[d].logins += row.login_count ?? 1;
    activityByDate[d].lessons += row.lessons_completed ?? 0;
    activityByDate[d].quizzes += row.quizzes_completed ?? 0;
  });

  (completionsRes.data ?? []).forEach((row: any) => {
    const d = row.completed_at?.slice(0, 10);
    if (d) {
      if (!activityByDate[d]) activityByDate[d] = { xp: 0, logins: 0, lessons: 0, quizzes: 0 };
      activityByDate[d].lessons += 1;
      if (activityByDate[d].xp === 0) activityByDate[d].xp += 50;
    }
  });

  (quizAttemptsRes.data ?? []).forEach((row: any) => {
    const d = row.completed_at?.slice(0, 10);
    if (d) {
      if (!activityByDate[d]) activityByDate[d] = { xp: 0, logins: 0, lessons: 0, quizzes: 0 };
      activityByDate[d].quizzes += 1;
      if (activityByDate[d].xp === 0) activityByDate[d].xp += 30;
    }
  });

  const days: HeatmapDay[] = [];
  const today = new Date();
  for (let i = weeks * 7 - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().slice(0, 10);

    const act = activityByDate[dateStr] ?? { xp: 0, logins: 0, lessons: 0, quizzes: 0 };
    const totalScore = act.xp + (act.logins * 20) + (act.lessons * 50) + (act.quizzes * 30);

    let intensity: 0 | 1 | 2 | 3 = 0;
    if (totalScore >= 120) intensity = 3;
    else if (totalScore >= 50) intensity = 2;
    else if (totalScore > 0) intensity = 1;

    days.push({
      date: dateStr,
      intensity,
      xpEarned: act.xp,
      loginCount: act.logins,
      activityCount: act.lessons + act.quizzes + (act.logins > 0 ? 1 : 0),
    });
  }
    return days;
  }, CACHE_TTL.HEATMAP);
}

/** Son 7 günün aktivitelerini 3 kaynaktan toplar */
async function buildRecentActivity(studentId: string): Promise<ActivityLogItem[]> {
  const since = new Date();
  since.setDate(since.getDate() - 7);
  const sinceStr = since.toISOString();

  const [lessonsRes, notesRes, aiRes] = await Promise.all([
    // 1. Tamamlanan dersler
    supabase
      .from('student_lesson_completions')
      .select('id, completed_at, lesson_id, lessons(title, courses(title))')
      .eq('student_id', studentId)
      .gte('completed_at', sinceStr)
      .order('completed_at', { ascending: false })
      .limit(5),
    // 2. Alınan notlar
    supabase
      .from('student_notes')
      .select('id, created_at, lesson_id, lessons(title, courses(title))')
      .eq('student_id', studentId)
      .gte('created_at', sinceStr)
      .order('created_at', { ascending: false })
      .limit(4),
    // 3. AI mentor sohbetleri
    supabase
      .from('ai_conversations')
      .select('id, created_at, title')
      .eq('student_id', studentId)
      .gte('created_at', sinceStr)
      .order('created_at', { ascending: false })
      .limit(4),
  ]);

  const items: ActivityLogItem[] = [];

  // Tamamlanan dersler
  for (const row of lessonsRes.data ?? []) {
    const r = row as any;
    const lessonTitle = r.lessons?.title ?? 'Ders';
    const courseTitle = r.lessons?.courses?.title ?? '';
    items.push({
      id: `lesson-${r.id}`,
      type: 'lesson' as ActivityType,
      title: lessonTitle,
      description: courseTitle ? `${courseTitle} — Dersi tamamladı` : 'Dersi tamamladı',
      occurredAt: r.completed_at,
    });
  }

  // Notlar
  for (const row of notesRes.data ?? []) {
    const r = row as any;
    const lessonTitle = r.lessons?.title ?? 'Not';
    const courseTitle = r.lessons?.courses?.title ?? '';
    items.push({
      id: `note-${r.id}`,
      type: 'note' as ActivityType,
      title: lessonTitle,
      description: courseTitle ? `${courseTitle} — Not aldı` : 'Not aldı',
      occurredAt: r.created_at,
    });
  }

  // AI sohbetleri
  for (const row of aiRes.data ?? []) {
    const r = row as any;
    items.push({
      id: `ai-${r.id}`,
      type: 'ai_chat' as ActivityType,
      title: r.title ?? 'AI Mentor Sorusu',
      description: 'AI Mentor ile sohbet etti',
      occurredAt: r.created_at,
    });
  }

  // Tarihe göre sırala (yeniden eskiye)
  items.sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());

  return items.slice(0, 8);
}

/** Yaş grubuna göre kursun uygun olup olmadığını kontrol eder */
function isAgeCompatible(userAge: number | null, courseAgeCategory: string | null | undefined): boolean {
  if (!userAge || !courseAgeCategory) return true; // Kullanıcının yaşı yoksa veya kursun yaş kategorisi belirtilmemişse uyumludur.

  if (courseAgeCategory === '7-9') {
    return userAge >= 7 && userAge <= 9;
  }
  if (courseAgeCategory === '10-13') {
    return userAge >= 10 && userAge <= 13;
  }
  if (courseAgeCategory === '14-17') {
    return userAge >= 14 && userAge <= 17;
  }
  return true;
}

/** Yaşa göre önerilen kursları çeker (henüz başlanmamış veya az ilerlenenler) */
async function buildSuggestedCourses(studentId: string, age: number | null): Promise<Course[]> {
  return queryCache.dedupe(`suggested:${studentId}:${age}`, async () => {
    const difficulties = getDifficultyForAge(age);

    // Her iki sorguyu da PARALEL çalıştır
    const [progressResult, coursesResult] = await Promise.all([
      supabase
        .from('student_course_progress')
        .select('course_id')
        .eq('student_id', studentId),
      supabase
        .from('courses')
        .select('id, title, description, category, difficulty, cover_image_url, order_index, age_category')
        .in('difficulty', difficulties)
        .order('order_index', { ascending: true })
        .limit(50),
    ]);

    const activeCourseIds = new Set((progressResult.data ?? []).map((p: any) => p.course_id));

    const allCourses: Course[] = (coursesResult.data ?? []).map((row: any) => ({
      id: row.id,
      title: row.title,
      description: row.description,
      category: row.category,
      difficulty: row.difficulty,
      cover_image_url: row.cover_image_url,
      order_index: row.order_index,
      age_category: row.age_category,
    }));

    // Yaş uyumluluğuna göre filtrele
    const compatibleCourses = allCourses.filter(c => isAgeCompatible(age, c.age_category));

    // Henüz başlanmayanları öncelikle göster
    const notStarted = compatibleCourses.filter(c => !activeCourseIds.has(c.id));
    const started = compatibleCourses.filter(c => activeCourseIds.has(c.id));

    return [...notStarted, ...started].slice(0, 4);
  }, CACHE_TTL.COURSES);
}

interface ProgressWithCourse {
  course_id: string;
  progress_percent: number;
  courses: { title: string; category: string } | null;
}

export function useDashboardData() {
  const userId = useAuthStore((s) => s.session?.user.id);
  const profile = useAuthStore((s) => s.profile);
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    async function load() {
      try {
        const [progressRes, completionsCountRes, submissionsRes, quizAttemptsRes, heatmapDays, recentActivity, suggestedCourses] = await Promise.all([
          supabase
            .from('student_course_progress')
            .select('id, status, progress_percent, course_id, courses(title, category, lessons(id))')
            .eq('student_id', userId)
            .eq('status', 'in_progress')
            .limit(6),
          supabase
            .from('student_lesson_completions')
            .select('id', { count: 'exact', head: true })
            .eq('student_id', userId),
          supabase
            .from('submissions')
            .select('id', { count: 'exact', head: true })
            .eq('student_id', userId),
          supabase
            .from('quiz_attempts')
            .select('id', { count: 'exact', head: true })
            .eq('student_id', userId),
          buildRealHeatmap(userId!, 48),
          buildRecentActivity(userId!),
          buildSuggestedCourses(userId!, profile?.age ?? null),
        ]);

        if (cancelled) return;

        if (progressRes.error) throw progressRes.error;
        if (submissionsRes.error) throw submissionsRes.error;
        if (quizAttemptsRes.error) throw quizAttemptsRes.error;

        const activeCourses: ActiveCourseSummary[] = (progressRes.data ?? []).map((row: any, i) => {
          const totalLessons = row.courses?.lessons?.length ?? 0;
          const progressPercent = row.progress_percent ?? 0;
          const completedLessons = Math.round((progressPercent * totalLessons) / 100);
          const estimatedHours = Math.ceil(totalLessons * 0.5);

          return {
            id: row.course_id,
            title: row.courses?.title ?? 'Bilinmeyen Kurs',
            icon: COURSE_ICONS[row.courses?.category ?? ''] ?? '📘',
            gradient: GRADIENTS[i % GRADIENTS.length],
            completedLessons,
            totalLessons,
            estimatedHours,
            progressPercent,
          };
        });

        setData({
          stats: {
            completedLessons: completionsCountRes.count ?? 0,
            completedLessonsDeltaWeek: 0,
            solvedQuizzes: quizAttemptsRes.count ?? 0,
            solvedQuizzesDeltaWeek: 0,
            totalStudyHours: Math.ceil((completionsCountRes.count ?? 0) * 0.5),
            totalStudyHoursDeltaWeek: 0,
            completedProjects: submissionsRes.count ?? 0,
            completedProjectsDeltaWeek: 0,
          },
          activeCourses,
          dailyTasks: STATIC_DAILY_TASKS.map((t) => ({ ...t, isCompleted: false })),
          recentActivity,
          heatmap: heatmapDays,
          suggestedCourses,
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Dashboard verisi yüklenemedi.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [userId, profile?.age]);

  return { data, isLoading, error };
}
